import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Grid,
    Pagination,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    useLearningPosts,
    useLearningPostsByTags,
    useLearningTags
} from '../../../hooks/useLearning';
import LearningCard from './LearningCard';
import LearningTagsFilterCard from './components/LearningTagsFilterCard';

function Aprender() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedTags, setSelectedTags] = useState([]);
    const { data: tags = [] } = useLearningTags();
    const learningAll = useLearningPosts();
    const learningByTags = useLearningPostsByTags(selectedTags);
    const setAllPaginationModel = learningAll.setPaginationModel;
    const setFilteredPaginationModel = learningByTags.setPaginationModel;

    const {
        data: contents,
        isLoading,
        error,
        count,
        paginationModel,
        setPaginationModel
    } = selectedTags.length > 0 ? learningByTags : learningAll;

    useEffect(() => {
        const resetPagination =
            selectedTags.length > 0 ? setFilteredPaginationModel : setAllPaginationModel;

        resetPagination((previous) =>
            previous.page === 0 ? previous : { ...previous, page: 0 }
        );
    }, [selectedTags, setAllPaginationModel, setFilteredPaginationModel]);

    const handlePageChange = (_, newPage) => {
        setPaginationModel((previous) => ({ ...previous, page: newPage - 1 }));
    };

    return (
        <Box sx={{ minHeight: 'calc(100vh - 80px)', p: 2, bgcolor: '#F4F6F8' }}>
            <Box
                component="header"
                sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
            >
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/aprender/criar')}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 205,
                        height: 46,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        textTransform: 'none'
                    }}
                >
                    {t('learning.add_new')}
                </Button>
            </Box>

            <Card
                variant="outlined"
                sx={{
                    p: 4,
                    borderColor: '#E0E5E9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <LearningTagsFilterCard
                            tags={tags}
                            selectedTags={selectedTags}
                            onChange={setSelectedTags}
                        />
                    </Grid>

                    {isLoading ? (
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                p={4}
                            >
                                <CircularProgress />
                            </Box>
                        </Grid>
                    ) : error ? (
                        <Grid item xs={12}>
                            <Typography color="error" align="center">
                                {t('learning.load_error')}
                            </Typography>
                        </Grid>
                    ) : contents.length === 0 ? (
                        <Grid item xs={12}>
                            <Typography align="center">
                                {t('learning.none_available')}
                            </Typography>
                        </Grid>
                    ) : (
                        <>
                            {contents.map((content) => (
                                <Grid
                                    key={content.uuid}
                                    item
                                    lg={4}
                                    md={6}
                                    sm={6}
                                    xs={12}
                                >
                                    <LearningCard
                                        content={content}
                                        onClick={() =>
                                            navigate(`/aprender/${content.uuid}`)
                                        }
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <Pagination
                                        count={Math.ceil(
                                            count / paginationModel.pageSize
                                        )}
                                        page={paginationModel.page + 1}
                                        onChange={handlePageChange}
                                    />
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Card>
        </Box>
    );
}

export default Aprender;
